import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Globe, Search, Power, SlidersHorizontal, PauseCircle, PlayCircle } from "lucide-react";
import EntityConfigDialog from "@/components/admin/EntityConfigDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth-context";

const toNum = (v) => (v === "" || v == null ? 0 : Number(v));
const EMPTY = { name: "", code: "", banca_id: "", commission_pct: 0, currency: "BS", currencies: ["BS"], active: true, sales_limit_bs: 0, enable_web_access: false };
const WEB_ACCESS_TEMP_PASSWORD = "Connect2026*";
const CURRENCY_LABELS = { BS: "Bolívares (Bs)", USD: "Dólares (USD)", COP: "Pesos (COP)" };

const fmtLimit = (v) => (!v || Number(v) <= 0) ? "Ilimitado" : `${Number(v).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Bs`;

const StatusBadge = ({ e, testid }) => {
  const [label, cls] = !e.active ? ["Inactivo", "border-rose-500/40 text-rose-500 dark:text-rose-300 bg-rose-500/10"]
    : e.suspended ? ["Suspendido", "border-amber-500/40 text-amber-600 dark:text-amber-300 bg-amber-500/10"]
      : ["Activo", "border-emerald-500/40 text-emerald-600 dark:text-emerald-300 bg-emerald-500/10"];
  return <span data-testid={testid} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>{label}</span>;
};

export default function GruposPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cloneFor, setCloneFor] = useState(null);
  const [cloneForm, setCloneForm] = useState({ target_name: "", target_code: "" });
  const [search, setSearch] = useState("");
  const [toggleTarget, setToggleTarget] = useState(null);
  const [configFor, setConfigFor] = useState(null);

  const submitSave = () => {
    if (!form.name?.trim() || !form.code?.trim()) { toast.error("Nombre y código son obligatorios"); return; }
    save.mutate({ ...form, name: form.name.trim(), code: form.code.trim() });
  };

  const { data: bancas = [] } = useQuery({ queryKey: ["bancas"], queryFn: async () => (await api.get("/bancas")).data });
  const { user } = useAuth();
  const isBancaOwner = user?.role === "banca_owner";
  const { data: grupos = [], isLoading } = useQuery({ queryKey: ["grupos"], queryFn: async () => (await api.get("/grupos")).data });
  const bancaMap = Object.fromEntries(bancas.map((b) => [b.id, b]));

  const save = useMutation({
    mutationFn: async (raw) => {
      const payload = { ...raw, commission_pct: toNum(raw.commission_pct), sales_limit_bs: toNum(raw.sales_limit_bs) };
      if (editing) return (await api.patch(`/grupos/${editing.id}`, payload)).data;
      return (await api.post("/grupos", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Grupo actualizado" : "Grupo creado");
      qc.invalidateQueries({ queryKey: ["grupos"] });
      setOpen(false); setEditing(null); setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/grupos/${id}`)).data,
    onSuccess: () => { toast.success("Grupo eliminado"); qc.invalidateQueries({ queryKey: ["grupos"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al eliminar"),
  });
  const clone = useMutation({
    mutationFn: async ({ id, payload }) => (await api.post(`/grupos/${id}/clone-config`, payload)).data,
    onSuccess: () => { toast.success("Grupo clonado"); qc.invalidateQueries({ queryKey: ["grupos"] }); setCloneFor(null); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al clonar"),
  });
  const suspend = useMutation({
    mutationFn: async ({ id, suspended }) => (await api.patch(`/grupos/${id}/suspension`, { suspended })).data,
    onSuccess: (r) => { toast.success(`Grupo ${r.suspended ? "suspendido" : "reanudado"}`); qc.invalidateQueries({ queryKey: ["agencias"] }); qc.invalidateQueries({ queryKey: ["grupos"] }); qc.invalidateQueries({ queryKey: ["taquillas"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error"),
  });
  const toggleStatus = useMutation({
    mutationFn: async (id) => (await api.patch(`/grupos/${id}/toggle-status`)).data,
    onSuccess: (g) => { toast.success(`Grupo ${g.active ? "activado" : "inactivado"}`); qc.invalidateQueries({ queryKey: ["grupos"] }); setToggleTarget(null); },
    onError: (e) => { toast.error(e?.response?.data?.detail || "Error"); setToggleTarget(null); },
  });

  const filtered = grupos.filter((g) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (g.name || "").toLowerCase().includes(q) || (g.code || "").toLowerCase().includes(q);
  });

  const openCreate = () => { setEditing(null); setForm({ ...EMPTY, banca_id: isBancaOwner ? (user?.banca_id || bancas[0]?.id || "") : (bancas[0]?.id || "") }); setOpen(true); };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, code: g.code, banca_id: g.banca_id, commission_pct: g.commission_pct, currency: g.currency || "BS", currencies: g.currencies?.length ? g.currencies : [g.currency || "BS"], active: g.active, sales_limit_bs: g.sales_limit_bs || 0, enable_web_access: false });
    setOpen(true);
  };
  const openClone = (g) => { setCloneFor(g); setCloneForm({ target_name: `${g.name} (copia)`, target_code: "" }); };

  return (
    <div className="space-y-4" data-testid="admin-table-grupos">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Grupos</h1>
          <p className="text-sm text-muted-foreground">Banca → <span className="text-primary font-medium">Grupo</span> → Agencia → Taquilla.</p>
        </div>
        <Button onClick={openCreate} data-testid="grupo-create-button"><Plus size={16} className="mr-1.5"/>Nuevo Grupo</Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o código…" className="pl-9" data-testid="grupo-search-input"/>
      </div>

      <Card className="fintech-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm fintech-table">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Banca</th>
                <th className="px-4 py-3">Moneda</th>
                <th className="px-4 py-3 text-right">Comisión %</th>
                <th className="px-4 py-3 text-right">Límite Diario</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="fintech-row" data-testid="grupo-skeleton-row">
                  <td colSpan={8} className="px-4 py-3"><Skeleton className="h-6 w-full"/></td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Sin grupos registrados.</td></tr>}
              {!isLoading && filtered.map((g) => (
                <tr key={g.id} className="fintech-row">
                  <td className="px-4 py-3 font-mono text-sky-600 dark:text-sky-300">{g.code}</td>
                  <td className="px-4 py-3 font-medium">{g.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{bancaMap[g.banca_id]?.name || "-"}</td>
                  <td className="px-4 py-3"><span className="text-xs font-semibold" data-testid={`grupo-currencies-${g.code}`}>{(g.currencies?.length ? g.currencies : [g.currency]).join(" / ")}</span></td>
                  <td className="px-4 py-3 text-right tabular-nums">{g.commission_pct}%</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`grupo-limit-${g.code}`}>{fmtLimit(g.sales_limit_bs)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={!!g.active} onCheckedChange={() => setToggleTarget(g)} title={g.active ? "Activo — clic para inactivar" : "Inactivo — clic para activar"} data-testid={`grupo-toggle-${g.code}`}/>
                      <StatusBadge e={g} testid={`grupo-status-${g.code}`}/>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setConfigFor(g)} title="Cupos y comisiones" data-testid={`grupo-config-${g.code}`}><SlidersHorizontal size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openClone(g)} title="Clonar" data-testid={`grupo-clone-${g.code}`}><Copy size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(g)} data-testid={`grupo-edit-${g.code}`}><Pencil size={16}/></Button>
                      <Button variant="outline" size="sm" className={`h-8 px-2.5 text-xs font-semibold ${g.suspended ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-300 hover:bg-emerald-500/10" : "border-amber-500/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10"}`} onClick={() => suspend.mutate({ id: g.id, suspended: !g.suspended })} disabled={suspend.isPending} data-testid={`grupo-suspend-${g.code}`}>
                        {g.suspended ? <><PlayCircle size={14} className="mr-1"/>Reanudar</> : <><PauseCircle size={14} className="mr-1"/>Suspender</>}
                      </Button>
                      <Button variant="ghost" size="icon" className="text-rose-300" onClick={() => { if (window.confirm(`Eliminar ${g.name}?`)) del.mutate(g.id); }} data-testid={`grupo-delete-${g.code}`}><Trash2 size={16}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal crear/editar */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar grupo" : "Nuevo grupo"}</DialogTitle></DialogHeader>
          <Tabs defaultValue="principales">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="principales" data-testid="grupo-tab-principales">Datos Principales</TabsTrigger>
              <TabsTrigger value="fiscales" data-testid="grupo-tab-fiscales">Datos Fiscales</TabsTrigger>
              <TabsTrigger value="config" data-testid="grupo-tab-config">Configuración</TabsTrigger>
            </TabsList>
            <TabsContent value="principales" className="pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="grupo-form-name"/></div>
                <div className="space-y-2"><Label>Código</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} data-testid="grupo-form-code"/></div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Banca (entidad superior)</Label>
                  {isBancaOwner ? (
                    <div className="rounded-md border-2 border-border bg-secondary/40 px-3 py-2 text-sm font-semibold" data-testid="grupo-form-banca-static">
                      {bancaMap[form.banca_id]?.name ? `${bancaMap[form.banca_id].name} (${bancaMap[form.banca_id].code})` : "Tu banca (sesión actual)"}
                      <span className="ml-2 text-[11px] font-normal text-muted-foreground">fija — sesión actual</span>
                    </div>
                  ) : (
                    <Select value={form.banca_id} onValueChange={(v) => setForm({ ...form, banca_id: v })}>
                      <SelectTrigger data-testid="grupo-form-banca"><SelectValue placeholder="Selecciona una banca"/></SelectTrigger>
                      <SelectContent>{bancas.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.code})</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Monedas habilitadas <span className="text-[11px] font-normal text-muted-foreground">(selección múltiple — solo las configuradas en la banca)</span></Label>
                  <div className="flex gap-2" data-testid="grupo-form-currencies">
                    {(bancaMap[form.banca_id]?.currencies?.length ? bancaMap[form.banca_id].currencies : ["BS", "USD", "COP"]).map((c) => {
                      const on = form.currencies.includes(c);
                      return (
                        <button key={c} type="button" data-testid={`grupo-form-currency-${c}`}
                          onClick={() => {
                            const next = on ? form.currencies.filter((x) => x !== c) : [...form.currencies, c];
                            if (next.length === 0) { toast.error("Selecciona al menos una moneda"); return; }
                            setForm({ ...form, currencies: next, currency: next[0] });
                          }}
                          className={`rounded-lg border-2 px-4 py-2 text-sm font-bold transition-colors ${on ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-secondary/60"}`}>
                          {CURRENCY_LABELS[c] || c}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} id="grupo-active"/>
                  <Label htmlFor="grupo-active">Activo</Label>
                </div>
                <div className="md:col-span-2 rounded-lg border-2 border-primary/30 bg-primary/5 p-3 space-y-3">
                  <div className="flex items-center gap-2">
                    <Globe size={16} className="text-primary"/>
                    <Switch checked={form.enable_web_access} onCheckedChange={(v) => setForm({ ...form, enable_web_access: v })} id="grupo-web" data-testid="grupo-form-web-access"/>
                    <Label htmlFor="grupo-web" className="text-sm font-semibold">Web Access — usuario de solo lectura</Label>
                  </div>
                  {form.enable_web_access && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="grupo-web-access-fields">
                      <div className="space-y-1">
                        <Label className="text-xs">Usuario</Label>
                        <Input readOnly value={`grp_${(form.code || "····").toLowerCase()}`} className="font-mono bg-secondary/60" data-testid="grupo-web-access-username"/>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Contraseña temporal</Label>
                        <Input readOnly value={WEB_ACCESS_TEMP_PASSWORD} className="font-mono bg-secondary/60 text-primary" data-testid="grupo-web-access-password"/>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
            <TabsContent value="fiscales" className="pt-3">
              <div className="rounded-lg border border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                Los datos fiscales (RIF, razón social, dirección) se configuran a nivel de <span className="text-foreground font-medium">Agencia</span>, que es la entidad facturable frente al punto de venta.
              </div>
            </TabsContent>
            <TabsContent value="config" className="pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Comisión general %</Label>
                  <Input type="number" step="0.1" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} onWheel={(e) => e.currentTarget.blur()} data-testid="grupo-form-commission"/>
                </div>
                <div className="space-y-2">
                  <Label>Límite de Venta Diario (Bs)</Label>
                  <Input type="number" min="0" step="1" value={form.sales_limit_bs} onChange={(e) => setForm({ ...form, sales_limit_bs: e.target.value })} onWheel={(e) => e.currentTarget.blur()} placeholder="Ilimitado (dejar vacío o 0)" data-testid="grupo-form-sales-limit"/>
                  <p className="text-[11px] text-muted-foreground">Tope de ventas del día para el grupo (suma de todas sus agencias). 0 = ilimitado.</p>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-muted-foreground">La comisión general aplica a todas las loterías del grupo. La diferencia con la comisión de la agencia es la utilidad diferencial del grupo.</p>
                </div>
              </div>
              {editing && (
                <div className="mt-4 rounded-lg border-2 border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold flex items-center gap-2"><SlidersHorizontal size={15} className="text-primary"/> Configuración de cupos y comisiones</div>
                    <div className="text-[11px] text-muted-foreground">Cupos, participación y comisiones por modalidad y divisa (Bs/USD). Se hereda a las entidades inferiores.</div>
                  </div>
                  <Button type="button" variant="outline" onClick={() => { setOpen(false); setConfigFor(editing); }} data-testid="grupo-config-tab-button">Configurar</Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submitSave} disabled={save.isPending} data-testid="grupo-form-submit">{editing ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal clonar */}
      <Dialog open={!!cloneFor} onOpenChange={(v) => !v && setCloneFor(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Clonar grupo {cloneFor?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Nombre del nuevo grupo</Label><Input value={cloneForm.target_name} onChange={(e) => setCloneForm({ ...cloneForm, target_name: e.target.value })} data-testid="grupo-clone-name"/></div>
            <div className="space-y-2"><Label>Código</Label><Input value={cloneForm.target_code} onChange={(e) => setCloneForm({ ...cloneForm, target_code: e.target.value.toUpperCase() })} data-testid="grupo-clone-code"/></div>
            <p className="text-xs text-muted-foreground">Se copian la comisión ({cloneFor?.commission_pct}%) y la moneda ({cloneFor?.currency}).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloneFor(null)}>Cancelar</Button>
            <Button onClick={() => clone.mutate({ id: cloneFor.id, payload: cloneForm })} disabled={clone.isPending || !cloneForm.target_code} data-testid="grupo-clone-submit">Clonar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirmar habilitar/deshabilitar */}
      <Dialog open={!!toggleTarget} onOpenChange={(v) => !v && setToggleTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="grupo-toggle-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Power size={18}/> {toggleTarget?.active ? "Inactivar" : "Activar"} grupo</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            ¿Confirmas {toggleTarget?.active ? "marcar como INACTIVO (baja permanente)" : "activar"} el grupo <span className="font-semibold text-foreground">{toggleTarget?.name}</span>? Esta acción quedará registrada en auditoría.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleTarget(null)}>Cancelar</Button>
            <Button onClick={() => toggleStatus.mutate(toggleTarget.id)} disabled={toggleStatus.isPending} data-testid="grupo-toggle-confirm">{toggleTarget?.active ? "Inactivar" : "Activar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {configFor && <EntityConfigDialog open={!!configFor} onClose={() => setConfigFor(null)} entityType="grupo" entity={configFor}/>}
    </div>
  );
}
